import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'

import { DrawerBodyWrapper, EmptyState } from '@base'
import { Button, SearchInput } from '@baseV2'
import { Spacings, Text } from '@styles'
import { toggleDrawer } from '@data/state/action/root'
import { DRAWER_TYPES, strings } from '@constants'
import { DrawerHeaderSlotContext } from '@global/Drawer/context'
import AddressCardV2 from './AddressCardV2'

const BodyWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
	padding-top: ${Spacings.SPACING_1B};
`

const LabelHeader = styled.span`
	font-size: ${Text.LARGE};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.text.primary};
`

const AddressListWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
`

const searchableText = (address) => {
	return [address?.address_id, address?.complete_address, address?.city, address?.state, address?.pincode]
		.filter(Boolean)
		.join(' ')
		.toLowerCase()
}

const SelectAddress = ({
	user,
	updateCallback,
	addToOrg = false,
	addToOrgCallback,
	selectedAddressId,
	showSearch = true,
}) => {
	const dispatch = useDispatch()
	const headerSlot = useContext(DrawerHeaderSlotContext)
	const [query, setQuery] = useState('')
	const selectedCardRef = useRef(null)

	const filteredAddresses = useMemo(() => {
		const addresses = user?.addresses ?? []
		const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
		if (!tokens.length) return addresses
		return addresses.filter((address) => {
			const haystack = searchableText(address)
			return tokens.every((token) => haystack.includes(token))
		})
	}, [user?.addresses, query])

	useEffect(() => {
		if (selectedCardRef.current) {
			selectedCardRef.current.scrollIntoView({ block: 'nearest' })
		}
	}, [selectedAddressId, filteredAddresses])

	const addAddressHandler = () => {
		dispatch(
			toggleDrawer(true, DRAWER_TYPES.ADD_NEW_ADDRESS, {
				addToNewAuction: true,
				updateCallback,
				label: strings('add_new_address'),
				addToNewEnquiry: true,
				addToOrgCallback,
				addToOrg,
			})
		)
	}

	const selectAddress = (address) => () => {
		updateCallback(address)
		dispatch(toggleDrawer(false))
	}

	return (
		<>
			{showSearch &&
				headerSlot &&
				createPortal(
					<SearchInput
						value={query}
						onChange={(e) => setQuery(e?.target?.value ?? '')}
						onClear={() => setQuery('')}
						placeholder={strings('search')}
						maxWidth='100%'
					/>,
					headerSlot
				)}
			<DrawerBodyWrapper>
				<BodyWrapper>
					<div>
						<Button variant='solid' color='primary' size='small' onClick={addAddressHandler}>
							{strings('add_new_address')}
						</Button>
					</div>
					<LabelHeader>{strings('select_from_existing_addresses')}</LabelHeader>
					{filteredAddresses?.length ? (
						<AddressListWrapper>
							{filteredAddresses.map((address, index) => {
								const isSelected = !!selectedAddressId && address?.address_id === selectedAddressId
								return (
									<div
										ref={isSelected ? selectedCardRef : null}
										onClick={selectAddress(address)}
										key={address?.address_id ?? index}
									>
										<AddressCardV2 address={address} selected={isSelected} />
									</div>
								)
							})}
						</AddressListWrapper>
					) : (
						<EmptyState noAction text={strings('no_address_found')} />
					)}
				</BodyWrapper>
			</DrawerBodyWrapper>
		</>
	)
}

export default SelectAddress
